import { authClient } from "@/lib/auth-clients";
import { single } from "@/lib/orpc-batch";
import { orpcClient } from "@/lib/orpc-client";

import { parseCanvas, type CanvasPayload, type SavedCanvas } from "./persist";

export async function sessionCompanyId(): Promise<string | null> {
  try {
    const { data } = await authClient.getSession();
    return (data as { companyId?: string | null } | null)?.companyId ?? null;
  } catch {
    return null;
  }
}

async function selfEmployeeId(companyId: string): Promise<string | null> {
  const { data } = await authClient.getSession();
  const session = data as {
    companyId?: string | null;
    employeeId?: string | null;
  } | null;
  if (!session?.employeeId || session.companyId !== companyId) return null;
  return session.employeeId;
}

export async function fetchRemoteCanvas(
  companyId: string,
): Promise<SavedCanvas | null | undefined> {
  try {
    const employeeId = await selfEmployeeId(companyId);
    if (!employeeId) return undefined;
    const self = await single(orpcClient.employee.read([{ id: employeeId }]));
    if (!self) return undefined;
    const layout = self.warRoomLayout as {
      widgets?: unknown;
      savedAt?: unknown;
    } | null;
    const canvas = parseCanvas(layout);
    if (canvas) return canvas;
    if (
      Array.isArray(layout?.widgets) &&
      layout.widgets.length === 0 &&
      typeof layout.savedAt === "string"
    ) {
      return {
        widgets: [],
        activeDatasetKey: null,
        mode: "light",
        savedAt: layout.savedAt,
      };
    }
    return null;
  } catch {
    return undefined;
  }
}

export async function pushRemoteCanvas(
  payload: CanvasPayload,
): Promise<boolean> {
  try {
    const employeeId = await selfEmployeeId(payload.companyId);
    if (!employeeId) return false;
    await orpcClient.employee.updateSelf({ warRoomLayout: payload });
    return true;
  } catch {
    return false;
  }
}
